import { FC } from "react";
import { Button, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import {
    BriefStatus,
    BuyerBrief,
    EVALUATION_GOAL_LABELS,
    STATUS_LABELS,
    SUPPLIER_TYPE_LABELS,
    Verdict,
    VERDICT_LABELS,
} from "../../../types/BuyerBriefTypes";
import { ListCard, ListCardField } from "../../../common/components/ListCard";
import { DateTime } from "../../../common/components/DateTime";
import { SvgIcon } from "../../../common/components/SvgIcon";

const { Link } = Typography;

const STATUS_COLOR_MAP = new Map([
    [BriefStatus.COMPLETED, "success"],
    [BriefStatus.PROCESSING, "processing"],
    [BriefStatus.FAILED, "error"],
    [BriefStatus.DRAFT, "default"],
]);

const VERDICT_COLOR_MAP = new Map([
    [Verdict.STRONG_YES, "green"],
    [Verdict.YES, "green"],
    [Verdict.CONDITIONAL, "gold"],
    [Verdict.RENEGOTIATE, "orange"],
    [Verdict.NO, "red"],
    [Verdict.STRONG_NO, "magenta"],
]);

function getStatusColor(status: BriefStatus) {
    return STATUS_COLOR_MAP.get(status) || "default";
}

function getVerdictColor(verdict: Verdict) {
    return VERDICT_COLOR_MAP.get(verdict) || "default";
}

interface BuyerBriefCardProps {
    brief: BuyerBrief;
    onClick?: () => void;
    onDelete?: (brief: BuyerBrief) => void;
}

export const BuyerBriefCard: FC<BuyerBriefCardProps> = ({ brief, onClick, onDelete }) => {
    const navigate = useNavigate();

    const defaultOnClick = brief.id ? () => navigate(`/superadmin/brief/${brief.id}`) : undefined;

    const fields: ListCardField[] = [
        {
            label: "Supplier",
            span: 2,
            content: (
                <Link strong ellipsis>
                    {brief.supplierName}
                </Link>
            ),
        },
        {
            label: "Type",
            span: 2,
            content: SUPPLIER_TYPE_LABELS[brief.supplierType],
        },
        {
            label: "Goal",
            span: 2,
            content: EVALUATION_GOAL_LABELS[brief.evaluationGoal],
        },
        {
            label: "Status",
            content: <Tag color={getStatusColor(brief.status)}>{STATUS_LABELS[brief.status]}</Tag>,
        },
        {
            label: "Recommendation",
            content: brief.verdict ? (
                <Tag color={getVerdictColor(brief.verdict)}>{VERDICT_LABELS[brief.verdict]}</Tag>
            ) : (
                "-"
            ),
        },
        {
            label: "Created",
            content: brief.recordCreationDate ? (
                <DateTime date={new Date(brief.recordCreationDate)} showTime={false} />
            ) : (
                "-"
            ),
        },
    ];

    const actions = onDelete ? (
        <Button
            type="primary"
            size="small"
            icon={<SvgIcon name="trash" size={14} />}
            onClick={() => onDelete(brief)}
            danger
        />
    ) : undefined;

    return (
        <ListCard
            onClick={onClick || defaultOnClick}
            logo={brief.briefOutput?.logo}
            logoFallback={brief.supplierName}
            fields={fields}
            actions={actions}
            style={{ background: "#fafafa", borderBottom: "1px solid #d9d9d9" }}
        />
    );
};
